// src/components/InvestmentCalculator.jsx
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  DollarSign, 
  Calendar, 
  TrendingUp, 
  Target,
  Zap,
  Calculator
} from 'lucide-react'

const InvestmentCalculator = ({ packages, onClose }) => {
  const [formData, setFormData] = useState({
    packageId: '',
    investmentAmount: 1000,
    investmentPeriod: 30
  })

  const [results, setResults] = useState(null)
  const [selectedPackage, setSelectedPackage] = useState(null)

  // Set default package if packages are provided
  useEffect(() => {
    if (packages && packages.length > 0 && !formData.packageId) {
      const defaultPackage = packages[0]
      setFormData(prev => ({
        ...prev,
        packageId: defaultPackage.id
      }))
      setSelectedPackage(defaultPackage)
    }
  }, [packages])

  // Update selected package when packageId changes
  useEffect(() => {
    if (formData.packageId && packages) {
      const pkg = packages.find(p => p.id === formData.packageId)
      setSelectedPackage(pkg)
    }
  }, [formData.packageId, packages])

  // Calculate results when form data changes
  useEffect(() => {
    if (selectedPackage && formData.investmentAmount > 0) {
      calculateReturns()
    }
  }, [formData, selectedPackage])

  const calculateReturns = () => {
    if (!selectedPackage) return

    const dailyROI = selectedPackage.dailyROI || 2.5
    const investment = parseFloat(formData.investmentAmount)
    const days = parseInt(formData.investmentPeriod)

    const dailyEarnings = investment * (dailyROI / 100)
    const totalEarnings = dailyEarnings * days
    const totalValue = investment + totalEarnings
    const roiPercentage = (totalEarnings / investment) * 100

    setResults({
      dailyEarnings,
      totalEarnings,
      totalValue,
      roiPercentage,
      dailyROI
    })
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const investmentRanges = [
    { label: '$100', value: 100 },
    { label: '$500', value: 500 },
    { label: '$1,000', value: 1000 },
    { label: '$5,000', value: 5000 },
    { label: '$10,000', value: 10000 }
  ]

  const periodRanges = [
    { label: '7 days', value: 7 },
    { label: '30 days', value: 30 },
    { label: '90 days', value: 90 },
    { label: '180 days', value: 180 },
    { label: '365 days', value: 365 }
  ]

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className="w-full"
    >
      <div className="space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Calculator className="w-8 h-8 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              Investment Calculator
            </h2>
          </div>
          <p className="text-gray-600 dark:text-gray-400">
            Calculate your potential returns based on different investment scenarios
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Input Section */}
          <div className="space-y-6">
            {/* Package Selection */}
            {packages && packages.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Select Investment Package
                </label>
                <div className="grid gap-3">
                  {packages.map((pkg) => (
                    <button
                      key={pkg.id}
                      onClick={() => handleInputChange('packageId', pkg.id)}
                      className={`p-4 rounded-2xl border-2 text-left transition-all ${
                        formData.packageId === pkg.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {pkg.name}
                        </span>
                        <span className="text-lg font-bold text-blue-600">
                          {pkg.dailyROI}%
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        Daily ROI • {pkg.termDays} Days
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Investment Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Investment Amount
              </label>
              
              {/* Quick Select Buttons */}
              <div className="flex flex-wrap gap-2 mb-4">
                {investmentRanges.map((range) => (
                  <button
                    key={range.value}
                    onClick={() => handleInputChange('investmentAmount', range.value)}
                    className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                      formData.investmentAmount === range.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>

              {/* Custom Amount Input */}
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="number"
                  value={formData.investmentAmount}
                  onChange={(e) => handleInputChange('investmentAmount', e.target.value)}
                  min="100"
                  max="1000000"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                  placeholder="Enter investment amount"
                />
              </div>

              {/* Range Slider */}
              <input
                type="range"
                min="100"
                max="100000"
                step="100"
                value={formData.investmentAmount}
                onChange={(e) => handleInputChange('investmentAmount', parseInt(e.target.value))}
                className="w-full mt-4"
              />
            </div>

            {/* Investment Period */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Investment Period (Days)
              </label>
              
              {/* Quick Select Buttons */}
              <div className="flex flex-wrap gap-2 mb-4">
                {periodRanges.map((range) => (
                  <button
                    key={range.value}
                    onClick={() => handleInputChange('investmentPeriod', range.value)}
                    className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                      formData.investmentPeriod === range.value
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {range.label}
                  </button>
                ))}
              </div>

              {/* Custom Period Input */}
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="number"
                  value={formData.investmentPeriod}
                  onChange={(e) => handleInputChange('investmentPeriod', e.target.value)}
                  min="7"
                  max="365"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                  placeholder="Investment period in days"
                />
              </div>

              {/* Range Slider */}
              <input
                type="range"
                min="7"
                max="365"
                step="1"
                value={formData.investmentPeriod}
                onChange={(e) => handleInputChange('investmentPeriod', parseInt(e.target.value))}
                className="w-full mt-4"
              />
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-6">
            {results && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-gradient-to-br from-blue-600 to-purple-600 rounded-3xl p-6 text-white"
              >
                <h3 className="text-xl font-bold mb-6 text-center">Projected Returns</h3>
                
                <div className="space-y-4">
                  {/* Daily Earnings */}
                  <div className="flex items-center justify-between p-4 bg-white/10 rounded-2xl">
                    <div className="flex items-center space-x-3">
                      <Zap className="w-5 h-5 text-yellow-400" />
                      <div>
                        <div className="text-sm opacity-90">Daily Earnings</div>
                        <div className="text-2xl font-bold">{formatCurrency(results.dailyEarnings)}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm opacity-90">Daily ROI</div>
                      <div className="text-lg font-bold">{results.dailyROI}%</div>
                    </div>
                  </div>

                  {/* Total Earnings */}
                  <div className="flex items-center justify-between p-4 bg-white/10 rounded-2xl">
                    <div className="flex items-center space-x-3">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      <div>
                        <div className="text-sm opacity-90">Total Earnings</div>
                        <div className="text-2xl font-bold">{formatCurrency(results.totalEarnings)}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm opacity-90">ROI</div>
                      <div className="text-lg font-bold">{results.roiPercentage.toFixed(1)}%</div>
                    </div>
                  </div>

                  {/* Total Value */}
                  <div className="flex items-center justify-between p-4 bg-white/10 rounded-2xl">
                    <div className="flex items-center space-x-3">
                      <Target className="w-5 h-5 text-orange-400" />
                      <div>
                        <div className="text-sm opacity-90">Total Value</div>
                        <div className="text-2xl font-bold">{formatCurrency(results.totalValue)}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm opacity-90">Period</div>
                      <div className="text-lg font-bold">{formData.investmentPeriod} days</div>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="mt-6 p-4 bg-white/10 rounded-2xl text-center">
                  <div className="text-sm opacity-90 mb-2">Your investment of</div>
                  <div className="text-xl font-bold mb-2">{formatCurrency(formData.investmentAmount)}</div>
                  <div className="text-sm opacity-90">
                    could grow to <span className="font-bold text-green-300">{formatCurrency(results.totalValue)}</span> in {formData.investmentPeriod} days
                  </div>
                </div>
              </motion.div>
            )}

            {/* Call to Action */}
            <div className="text-center">
              <button className="w-full bg-gradient-to-r from-yellow-400 to-orange-400 hover:from-yellow-500 hover:to-orange-500 text-gray-900 font-bold py-4 px-6 rounded-xl transition-all transform hover:scale-105">
                Start Investing Now
              </button>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">
                Calculations are estimates. Actual returns may vary based on market conditions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
}

export default InvestmentCalculator